import React, { useState } from "react";
import { auditApi } from "../services/api";

interface ResidentNote {
  name: string;
  note: string;
}

interface TaskLine {
  task: string;
  done: boolean;
}

export interface HandoverRecord {
  id: string;
  shift: string;
  date: string;
  residents: ResidentNote[];
  medications: string;
  incidents: string;
  tasks: TaskLine[];
  equipment: string;
  concerns: string;
  evidence: string[];
  signedBy: string;
  signedRole: string;
  savedAt: string;
}

const STORE_KEY = "handover_records";

function loadHistory(): HandoverRecord[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

export default function ShiftHandover({ user }: { user?: any }) {
  const [shift, setShift] = useState("Early");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [residents, setResidents] = useState<ResidentNote[]>([{ name: "", note: "" }]);
  const [medications, setMedications] = useState("");
  const [incidents, setIncidents] = useState("");
  const [tasks, setTasks] = useState<TaskLine[]>([{ task: "", done: false }]);
  const [equipment, setEquipment] = useState("");
  const [concerns, setConcerns] = useState("");
  const [evidence, setEvidence] = useState<string[]>([]);
  const [signedBy, setSignedBy] = useState(user && user.name ? user.name : "");
  const [signedRole, setSignedRole] = useState(user && user.role ? user.role : "");
  const [history, setHistory] = useState<HandoverRecord[]>(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [message, setMessage] = useState("");

  const attach = async (file: File) => {
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", file.type.startsWith("video") ? "video" : "photo");
      form.append("context", "handover");
      await auditApi.uploadEvidence(form);
      setEvidence(current => [...current, `${file.name} (uploaded)`]);
    } catch (error) {
      setEvidence(current => [...current, `${file.name} (saved locally)`]);
    }
  };

  const saveHandover = () => {
    if (!signedBy) {
      setMessage("Please sign the handover before saving.");
      return;
    }

    const record: HandoverRecord = {
      id: `ho-${Date.now()}`,
      shift,
      date,
      residents,
      medications,
      incidents,
      tasks,
      equipment,
      concerns,
      evidence,
      signedBy,
      signedRole,
      savedAt: new Date().toISOString(),
    };

    const next = [record, ...history];

    setHistory(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
    setMessage("Handover saved.");
    setResidents([{ name: "", note: "" }]);
    setTasks([{ task: "", done: false }]);
    setMedications("");
    setIncidents("");
    setEquipment("");
    setConcerns("");
    setEvidence([]);
  };
  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Shift Handover</h2>
          <p className="text-sm text-slate-600">Full shift handover with residents, medications, incidents, tasks and photo or video evidence.</p>
        </div>
        <button onClick={() => setShowHistory(!showHistory)} className="rounded-lg border border-blue-900 px-4 py-2 text-sm font-medium text-blue-900">
          {showHistory ? "Back to form" : `History (${history.length})`}
        </button>
      </div>

      {showHistory ? (
        <div className="space-y-3">
          {history.map(record => (
            <div key={record.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{record.shift} shift - {record.date}</div>
                <div className="text-sm text-slate-600">{record.signedBy} ({record.signedRole})</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">Saved {new Date(record.savedAt).toLocaleString()} | Residents {record.residents.length} | Tasks {record.tasks.length} | Evidence {record.evidence.length}</div>
            </div>
          ))}
          {history.length === 0 ? <div className="text-sm text-slate-500">No handovers recorded yet.</div> : null}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <select value={shift} onChange={event => setShift(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option>Early</option>
              <option>Late</option>
              <option>Night</option>
            </select>
            <input type="date" value={date} onChange={event => setDate(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Residents</h3>
            <div className="space-y-2">
              {residents.map((row, index) => (
                <div key={index} className="flex gap-2">
                  <input value={row.name} onChange={event => setResidents(current => current.map((r, i) => i === index ? { ...r, name: event.target.value } : r))} placeholder="Resident" className="w-40 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                  <input value={row.note} onChange={event => setResidents(current => current.map((r, i) => i === index ? { ...r, note: event.target.value } : r))} placeholder="Mood, intake, wellbeing, changes" className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              ))}
            </div>
            <button onClick={() => setResidents(current => [...current, { name: "", note: "" }])} className="mt-2 rounded-lg border border-slate-300 px-3 py-1 text-sm">Add resident line</button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-slate-900">Medications</h3>
              <textarea value={medications} onChange={event => setMedications(event.target.value)} rows={4} placeholder="MAR status, refusals, missed doses, pharmacy orders" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-slate-900">Incidents</h3>
              <textarea value={incidents} onChange={event => setIncidents(event.target.value)} rows={4} placeholder="Falls, injuries, safeguarding concerns, relative visits" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-slate-900">Equipment</h3>
              <textarea value={equipment} onChange={event => setEquipment(event.target.value)} rows={4} placeholder="Faults, repairs raised, hoist or bed issues" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-slate-900">Concerns</h3>
              <textarea value={concerns} onChange={event => setConcerns(event.target.value)} rows={4} placeholder="Anything the next shift must watch or follow up" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Tasks To Hand Over</h3>
            <div className="space-y-2">
              {tasks.map((row, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input type="checkbox" checked={row.done} onChange={event => setTasks(current => current.map((t, i) => i === index ? { ...t, done: event.target.checked } : t))} />
                  <input value={row.task} onChange={event => setTasks(current => current.map((t, i) => i === index ? { ...t, task: event.target.value } : t))} placeholder="Task for next shift" className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              ))}
            </div>
            <button onClick={() => setTasks(current => [...current, { task: "", done: false }])} className="mt-2 rounded-lg border border-slate-300 px-3 py-1 text-sm">Add task</button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 font-semibold text-slate-900">Evidence</h3>
            <div className="flex flex-wrap gap-2">
              <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1 text-sm">Add photo<input type="file" accept="image/*" capture="environment" className="hidden" onChange={event => { const file = event.target.files && event.target.files[0]; if (file) attach(file); }} /></label>
              <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1 text-sm">Add video<input type="file" accept="video/*" className="hidden" onChange={event => { const file = event.target.files && event.target.files[0]; if (file) attach(file); }} /></label>
            </div>
            {evidence.length > 0 ? <div className="mt-2 text-xs text-slate-500">{evidence.join(", ")}</div> : null}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 font-semibold text-slate-900">Signature</h3>
            <div className="flex flex-wrap gap-2">
              <input value={signedBy} onChange={event => setSignedBy(event.target.value)} placeholder="Full name" className="w-48 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={signedRole} onChange={event => setSignedRole(event.target.value)} placeholder="Role" className="w-48 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <button onClick={saveHandover} className="mt-3 rounded-lg bg-blue-900 px-5 py-2 text-sm font-medium text-white">Save handover</button>
            {message ? <div className="mt-2 text-sm text-blue-900">{message}</div> : null}
          </div>
        </div>
      )}
    </div>
  );
}